import React, { useLayoutEffect, useRef } from 'react';

import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_dark from "@amcharts/amcharts4/themes/dark";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { isTemplateTail } from 'typescript';

am4core.useTheme(am4themes_dark);
am4core.useTheme(am4themes_animated);

export interface IHeatMapChartDataItem {
    xAxisValue: string;
    yAxisValue: string;
    value: number;
  }

export interface IHeatMapChartProps {
    id: string;
    xAxisName: string;
    yAxisName: string;
    data: Array<IHeatMapChartDataItem>;
  }

const HeatMapChart: React.FC<IHeatMapChartProps> = (props: IHeatMapChartProps) => {
    useLayoutEffect(() => {
        let chart = am4core.create(`${props.id}`, am4charts.XYChart);
        chart.maskBullets = false;

        let xAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        let yAxis = chart.yAxes.push(new am4charts.CategoryAxis());

        xAxis.dataFields.category = props.xAxisName;
        yAxis.dataFields.category = props.yAxisName;

        xAxis.renderer.grid.template.disabled = true;
        xAxis.renderer.minGridDistance = 40;

        yAxis.renderer.grid.template.disabled = true;
        yAxis.renderer.inversed = true;
        yAxis.renderer.minGridDistance = 30;

        // Configure y-axis labels to strip out the sorting prefix
        yAxis.renderer.labels.template.adapter.add("text", function(text, target) {
            if (target.dataItem && target.dataItem.categories) {
                const label = target.dataItem.properties.category;
                if (label && label.indexOf("_") > -1) {
                    const parts = label.split("_");
                    if (parts.length > 1) {
                        return parts[1];  // Return just the time portion (e.g., "12am")
                    }
                }
            }
            return text;
        });

        let series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.categoryX = props.xAxisName;
        series.dataFields.categoryY = props.yAxisName;
        series.dataFields.value = "value";
        series.sequencedInterpolation = true;
        series.defaultState.transitionDuration = 3000;

        let bgColor = new am4core.InterfaceColorSet().getFor("background");

        let columnTemplate = series.columns.template;
        columnTemplate.strokeWidth = 1;
        columnTemplate.strokeOpacity = 0.2;
        columnTemplate.stroke = bgColor;

        // Configure tooltip with adapter to properly format time
        columnTemplate.adapter.add("tooltipText", function(text, target) {
            if (target.dataItem) {
                const weekday = target.dataItem.categories.categoryX;
                const timeValue = target.dataItem.categories.categoryY;
                const value = target.dataItem.values.value.value;

                const timeParts = timeValue.split("_");
                const timeDisplay = timeParts.length > 1 ? timeParts[1] : timeValue;
                return `${weekday}, ${timeDisplay}: ${value}`;
            }
            return "";
        });

        columnTemplate.width = am4core.percent(100);
        columnTemplate.height = am4core.percent(100);

        series.heatRules.push({
            target: columnTemplate,
            property: "fill",
            min: am4core.color(bgColor),
            max: chart.colors.getIndex(0)
        });

        // heat legend
        let heatLegend = chart.bottomAxesContainer.createChild(am4charts.HeatLegend);
        heatLegend.width = am4core.percent(100);
        heatLegend.series = series;
        heatLegend.valueAxis.renderer.labels.template.fontSize = 9;
        heatLegend.valueAxis.renderer.minGridDistance = 30;

        // heat legend behavior
        series.columns.template.events.on("over", function(event) {
            handleHover(event.target);
        })

        series.columns.template.events.on("hit", function(event) {
            handleHover(event.target);
        })

        function handleHover(column: any) {
            if (!isNaN(column.dataItem.value)) {
                heatLegend.valueAxis.showTooltipAt(column.dataItem.value)
            }
            else {
                heatLegend.valueAxis.hideTooltip();
            }
        }

        series.columns.template.events.on("out", function(event) {
            heatLegend.valueAxis.hideTooltip();
        })

        let data: any[] = props.data.map(function (item) {
            let result: any = {}
            result[`${props.xAxisName}`] = item.xAxisValue;
            result[`${props.yAxisName}`] = item.yAxisValue;
            result["value"] = item.value;

            return result;
        });

        chart.data = data;
    
        return () => {
          chart.dispose();
        };
      }, [props]);
    
    return (
        <div className="HeatMapChart" id={props.id} style={{ width: "100%", height: "500px" }}></div>
    );
  }
  
  export default HeatMapChart;
